import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useScrollToSection } from "../../../app/useScrollToSection";
import { getParamAsInt, scrollToTop } from "../../../app/utils";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import RtkqResultViewer from "../../../components/RtkqResultViewer";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import ModalAlert from "../../../components/ModalAlert";
import { SelectDevelopmentView } from "../../../components/input/SelectDevelopmentView";
import Select from "../../../components/input/Select";
import Messages from "../../messages/Messages";
import PromptPanel from "../../prompts/PromptPanel";
import Organizer from "../organizing/Organizer";
import DraftEditor from "./DraftEditor";
import WritingAnalysis from "./WritingAnalysis";
import PeerReviews from "./PeerReviews";
import CommentedEssay from "./CommentedEssay";
import WritingTimer from "./WritingTimer";
import { SaveDraftResponse, WriteDraftViewModel, useLazyWriteDraftQuery } from "./draftingApiSlice";
import "./write.css";

export default function Write() {
	const [queryParams] = useSearchParams();
	const draftId = getParamAsInt(queryParams, "draftId");
	if (draftId === undefined) return null;

	const [view, setView] = useState<string>("legacy");
	const [autosaveFailure, setAutosaveFailure] = useState(false);
	const [data, setData] = useState<WriteDraftViewModel>();
	const [layout, setLayout] = useState<string>("previousDraft");
	const [layoutOptions, setLayoutOptions] = useState<SimpleOption[]>([]);
	const [isTimerRunning, setIsTimerRunning] = useState(true);
	const [timeExpired, setTimeExpired] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const [callWriteDraft, writeDraftResult] = useLazyWriteDraftQuery();

	const navigate = useNavigate();

	useScrollToSection();

	useEffect(() => {
		callWriteDraft(draftId)
			.unwrap()
			.then((result) => {
				setData(result);
			})
			.catch((error) => {
				console.log("error calling WriteDraft");
			});
	}, []);

	useEffect(() => {
		if (!data) return;
		if (data.isTimed && data.remainingTimeMs <= 0) {
			setTimeExpired(true);
		}
		initializeLayoutOptions(data);
	}, [data]);

	function initializeLayoutOptions(data: WriteDraftViewModel) {
		const options = [{ value: "none", label: "Current Draft Only" }];
		if (data.commentedEssay) {
			options.push({ value: "previousDraft", label: "Previous Draft" });
		}
		if (data.commentedEssay?.writingAnalysis) {
			options.push({ value: "writingAnalysis", label: "Writing Analysis" });
		}
		if (data.commentedEssay?.hasCompletedPeerReviews) {
			options.push({ value: "peerReview", label: "Peer Review" });
		}
		if (data.organizer?.organizerResponse) {
			options.push({ value: "organizer", label: "Graphic Organizer" });
		}
		setLayoutOptions(options);

		if (data.organizer?.isVisibleByDefault && data.organizer?.organizerResponse) {
			setLayout("organizer");
		} else if (data.commentedEssay?.writingAnalysis) {
			setLayout("writingAnalysis");
		} else if (data.commentedEssay) {
			setLayout("previousDraft");
		} else {
			setLayout("none");
		}
	}

	const draftEditorSection = useRef<HTMLDivElement | null>(null);
	const previousDraftSection = useRef<HTMLDivElement | null>(null);
	const analysisSection = useRef<HTMLDivElement | null>(null);
	const organizerSection = useRef<HTMLDivElement | null>(null);
	const peerReviewSection = useRef<HTMLDivElement | null>(null);
	const errorMessageSection = useRef<HTMLDivElement | null>(null);

	useLayoutEffect(() => {
		if (draftEditorSection.current && analysisSection.current) {
			const height = draftEditorSection.current.clientHeight;
			analysisSection.current.style.height = `${height}px`;
		}
	});

	function saveErrorHandler(resp: SaveDraftResponse) {
		setErrorMessage(resp.message ?? "There was an error saving your draft.");
		if (errorMessageSection.current) {
			errorMessageSection.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}

	function submitErrorHandler(resp: SaveDraftResponse) {
		setErrorMessage(resp.message ?? "There was an error submitting your draft.");
		if (errorMessageSection.current) {
			errorMessageSection.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}

	function autosaveFailureHandler(resp: SaveDraftResponse) {
		setAutosaveFailure(true);
		setIsTimerRunning(false);

		setErrorMessage(resp.message + " You are no longer able to edit this draft.");
		if (errorMessageSection.current) {
			errorMessageSection.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}

	function handleTimerExpire() {
		setTimeExpired(true);
	}

	function handleClearError() {
		setErrorMessage(undefined);
	}

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Practice Writing</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "legacy", label: "Legacy" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view == "legacy" && (
				<>
					{data && writeDraftResult.isSuccess && (
						<>
							<div className="mt-4">
								<p className="text-sm">
									Click <strong>Submit</strong> when finished to score your essay.
									{data.isTimed &&
										" If you have not submitted your essay when time expires, it will be submitted automatically."}
								</p>
							</div>
							<div className="flex justify-between">
								<PromptPanel
									name={data.promptName}
									showName={true}
									renderedText={data.renderedPromptText}
									stimulusMaterials={data.stimulusMaterials}
									signLanguageVideoURL={data.signLanguageVideoURL}
									showGenre={false}
									className="w-3/4"></PromptPanel>
								<div className="pt-12">
									{data.isTimed && (
										<WritingTimer
											isRunning={isTimerRunning}
											msToRun={data.remainingTimeMs}
											onTimeout={handleTimerExpire}
										/>
									)}
								</div>
							</div>
							<div ref={errorMessageSection}>
								<AlertMessage
									className="mt-8"
									type={AlertLevel.Error}
									message={errorMessage}
									showIcon={true}
									onClear={handleClearError}
								/>
							</div>
							{(data.organizer.htmlTemplate || data.commentedEssay) && (
								<Select
									value={layout}
									setValue={(v) => setLayout(v as string)}
									label={`Displaying: ${" "}`}
									simpleListBoxId="layout-dropdown"
									options={layoutOptions}
									fieldClasses="mt-8 flex items-center gap-2"
									labelClasses="inline !m-0"
									simpleListBoxClasses="inline w-48"
								/>
							)}
							<div className="flex space-x-4  mt-4">
								{layout == `previousDraft` && data.commentedEssay && (
									<div
										ref={previousDraftSection}
										className="basis-1/2 overflow-y-auto">
										<CommentedEssay commentedEssay={data.commentedEssay} />
									</div>
								)}
								{layout == `writingAnalysis` && data.commentedEssay && (
									<div
										ref={analysisSection}
										className="basis-1/2 overflow-y-auto">
										<WritingAnalysis
											analysis={data.commentedEssay.writingAnalysis}
										/>
									</div>
								)}
								{layout == `organizer` &&
									data.organizer &&
									data.organizer.htmlTemplate && (
										<div
											ref={organizerSection}
											className="basis-1/2 overflow-y-auto">
											<Organizer
												className="pt-2 relative"
												html={data.organizer.htmlTemplate}
												response={data.organizer.organizerResponse}
												readonly={true}
											/>
										</div>
									)}
								{layout == `peerReview` && data.commentedEssay && (
									<div
										ref={peerReviewSection}
										className="basis-1/2 overflow-y-auto">
										<PeerReviews
											className="pt-2 relative"
											draftId={data.draftId}
											readonly={true}
											peerReviews={
												data.commentedEssay.completedPeerReviewModels
											}
										/>
									</div>
								)}
								<div
									className={`min-w-[500px] ${
										layout != "none" ? "basis-1/2" : "basis-full"
									}`}>
									<DraftEditor
										className="draftEditor"
										draft={data}
										draftEditorSection={draftEditorSection}
										fullSize={layout == "none"}
										timeExpired={timeExpired}
										onSaveError={saveErrorHandler}
										onAutosaveFailure={autosaveFailureHandler}
										onSubmitError={submitErrorHandler}
									/>
								</div>
							</div>
							<hr className="my-4" />
							<section id="EssayMessages">
								<Messages draftId={draftId} disabled={autosaveFailure} />
							</section>
							<div className="mt-4">
								[<a onClick={scrollToTop}>Return to top</a>]
							</div>
						</>
					)}
				</>
			)}
			{autosaveFailure && (
				<ModalAlert
					open={true}
					title={"Error"}
					message={
						(errorMessage ?? "You are no longer able to edit this draft.") +
						" You will return to the home screen when you press OK."
					}
					type={AlertLevel.Error}
					confirmLabel={"OK"}
					onConfirm={() => navigate(`/home`)}
				/>
			)}
			{view == "raw" && <RtkqResultViewer heading="Write" result={writeDraftResult} />}
		</section>
	);
}
